import angular from 'angular';
import '../../shared.module';
import template from './seat-map.component.html';
import './seat-map.component.scss';
import {
  SEAT_KIND_DESCRIPTIONS,
  SEAT_KIND_ICONS,
  SEAT_KIND_LABELS,
  DEFAULT_STAGE_LABEL,
  seatAriaLabel,
  seatGrid,
  seatKindsIn,
  seatSurchargeLabel
} from './seat-map.helper';

export const SEAT_ZOOM_LEVELS = [0.6, 0.8, 1, 1.25, 1.5];
const BASE_SEAT_SIZE = 28;
const DEFAULT_ZOOM_INDEX = 2;
const MODES = ['select', 'edit', 'view'];
const ARROWS = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1]
};

class SeatMapController {
  constructor($element) {
    this.$element = $element;
    this.zoomIndex = DEFAULT_ZOOM_INDEX;
    this.grid = [];
    this.kinds = [];
    this.kindLabels = SEAT_KIND_LABELS;
    this.kindDescriptions = SEAT_KIND_DESCRIPTIONS;
    this.kindIcons = SEAT_KIND_ICONS;
  }

  $onInit() {
    this.mode = MODES.includes(this.mode) ? this.mode : 'view';
    this.showNumbers = this.numbers === undefined ? this.mode === 'edit' : Boolean(this.numbers);
    this.showLegend = this.legend !== false;
  }

  $onChanges(changes) {
    if (changes.map) {
      this.grid = seatGrid(this.map);
      this.kinds = seatKindsIn(this.map);
    }
  }

  get stageLabel() {
    return (this.map && this.map.palco) || DEFAULT_STAGE_LABEL;
  }

  get zoom() {
    return SEAT_ZOOM_LEVELS[this.zoomIndex];
  }

  get zoomPercent() {
    return Math.round(this.zoom * 100);
  }

  get seatSize() {
    return `${Math.round(BASE_SEAT_SIZE * this.zoom)}px`;
  }

  canZoomIn() {
    return this.zoomIndex < SEAT_ZOOM_LEVELS.length - 1;
  }

  canZoomOut() {
    return this.zoomIndex > 0;
  }

  zoomIn() {
    if (this.canZoomIn()) this.zoomIndex += 1;
  }

  zoomOut() {
    if (this.canZoomOut()) this.zoomIndex -= 1;
  }

  toggleNumbers() {
    this.showNumbers = !this.showNumbers;
  }

  isSelected(seat) {
    return Boolean(seat && seat.id && (this.selectedIds || []).includes(seat.id));
  }

  stateOf(seat) {
    if (this.isSelected(seat)) return 'selected';
    return seat.status === 'available' ? 'available' : 'unavailable';
  }

  ariaLabel(seat) {
    return seatAriaLabel(seat, this.isSelected(seat));
  }

  emptyLabel(cell, row) {
    const where = row.rotulo ? `Fileira ${row.rotulo}` : `Linha ${cell.linha}`;
    return `${where}, coluna ${cell.coluna}, sem assento (corredor)`;
  }

  surchargeOf(seat) {
    return seatSurchargeLabel(seat);
  }

  isInteractive(cell) {
    if (this.mode === 'edit') return true;
    if (this.mode !== 'select' || !cell.assento) return false;
    return this.isSelected(cell.assento) || cell.assento.status === 'available';
  }

  click(cell) {
    if (!this.isInteractive(cell)) return null;
    if (this.mode === 'edit') return this.onCellClick({ cell });
    const seat = cell.assento;
    if (this.isSelected(seat)) return this.onToggle({ seat, selected: false });
    const limit = Number(this.maxSelection) || 0;
    if (limit && (this.selectedIds || []).length >= limit) return this.onLimit({ seat });
    return this.onToggle({ seat, selected: true });
  }

  keydown(event, cell) {
    const step = ARROWS[event.key];
    if (!step) return false;
    event.preventDefault();
    const target = this.findNext(cell, step);
    if (target) target.focus();
    return Boolean(target);
  }

  findNext(cell, [rowStep, columnStep]) {
    const root = this.$element[0];
    let linha = cell.linha + rowStep;
    let coluna = cell.coluna + columnStep;
    const rows = this.map ? this.map.linhas : 0;
    const columns = this.map ? this.map.colunas : 0;
    while (linha >= 1 && linha <= rows && coluna >= 1 && coluna <= columns) {
      const button = root.querySelector(`[data-cell="${linha}-${coluna}"]`);
      if (button && !button.disabled) return button;
      linha += rowStep;
      coluna += columnStep;
    }
    return null;
  }
}

SeatMapController.$inject = ['$element'];

angular.module('app.shared').component('cpSeatMap', {
  bindings: {
    map: '<',
    selectedIds: '<',
    mode: '@',
    maxSelection: '<',
    numbers: '<',
    legend: '<',
    onToggle: '&',
    onCellClick: '&',
    onLimit: '&'
  },
  controller: SeatMapController,
  controllerAs: 'vm',
  template
});
